export type EnvNames =
  | 'default'
  | 'integration'
  | 'feature'
  | 'staging'
  | 'staging-np'
  | 'production';

type EnvConfigType = {
  ENV_NAME: EnvNames;
  API_BASEURL: string;
  SENTRY_DSN: string;
  SENTRY_DEBUG: boolean;
  AD_CLIENT_ID: string;
  AD_AUTHORITY_URL: string;
  AD_SCOPES: string;
  IDLE_TIMOUT_SECONDS: string;
  IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: string;
  MW_COMPONENTS_URI: string;
};

// Returns `value`, or `fallback` when `value` is missing, empty, or only
// whitespace.
//
// This helper replaces `??`-style fallbacks, which pass empty strings instead
// of a fallback. Important here because ENV vars with no value (e.g. `MY_VAR=`)
// come through as empty strings.
export function envOr(value: string | undefined, fallback: string) {
  return value?.trim() ? value : fallback;
}

export class EnvConfig {
  static configs = {
    default: {
      ENV_NAME: 'default',
      API_BASEURL: envOr(
        import.meta.env.VITE_API_BASEURL,
        'http://localhost:8000',
      ),
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: import.meta.env.VITE_SENTRY_DEBUG === 'true',
      AD_CLIENT_ID: envOr(
        import.meta.env.VITE_AD_CLIENT_ID,
        '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      ),
      AD_AUTHORITY_URL: envOr(
        import.meta.env.VITE_AD_AUTHORITY_URL,
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      ),
      AD_SCOPES: envOr(
        import.meta.env.VITE_AD_SCOPES,
        '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      ),
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://integration.rap-mwi.com',
    },
    integration: {
      ENV_NAME: 'integration',
      API_BASEURL: 'https://api.integration.snap.mwcomponents.com',
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: false,
      AD_CLIENT_ID: '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      AD_AUTHORITY_URL:
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      AD_SCOPES: '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://integration.rap-mwi.com',
    },
    feature: {
      ENV_NAME: 'feature',
      API_BASEURL: 'https://api.feature.snap.mwcomponents.com',
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: false,
      AD_CLIENT_ID: '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      AD_AUTHORITY_URL:
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      AD_SCOPES: '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://integration.rap-mwi.com',
    },
    staging: {
      ENV_NAME: 'staging',
      API_BASEURL: 'https://api.staging.snap.mwcomponents.com',
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: false,
      AD_CLIENT_ID: '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      AD_AUTHORITY_URL:
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      AD_SCOPES: '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://staging.rap-mwi.com',
    },
    // Staging in the new AWS account. Separate from `staging` rather than
    // sharing it because the two run side by side until DNS cutover and each
    // has its own API hostname.
    'staging-np': {
      ENV_NAME: 'staging-np',
      API_BASEURL: 'https://api.staging.np.snap.mwcomponents.com',
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: false,
      AD_CLIENT_ID: '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      AD_AUTHORITY_URL:
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      AD_SCOPES: '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://staging.rap-mwi.com',
    },
    production: {
      ENV_NAME: 'production',
      API_BASEURL: 'https://api.snap.mwcomponents.com',
      SENTRY_DSN: envOr(import.meta.env.VITE_SENTRY_DSN, ''),
      SENTRY_DEBUG: false,
      AD_CLIENT_ID: '5d86d05b-652e-4f4f-9281-41ac53347f7d',
      AD_AUTHORITY_URL:
        'https://login.microsoftonline.com/c70cfdb0-d54a-48c7-94b5-3ca6beb09191',
      AD_SCOPES: '5d86d05b-652e-4f4f-9281-41ac53347f7d/.default',
      IDLE_TIMOUT_SECONDS: '28800',
      IDLE_TIMEOUT_PROMPT_BEFORE_SECONDS: '120',
      MW_COMPONENTS_URI: 'https://www.mwcomponents.com',
    },
  } as const satisfies Record<EnvNames, EnvConfigType>;

  static getEnv(hostName = window.location.hostname): EnvNames {
    const envMap: Record<string, EnvNames> = {
      'integration.snap.mwcomponents.com': 'integration',
      'feature.snap.mwcomponents.com': 'feature',
      'staging.snap.mwcomponents.com': 'staging',
      'staging.np.snap.mwcomponents.com': 'staging-np',
      'snap.mwcomponents.com': 'production',
      localhost: 'default',
      default: 'default',
    };

    return envMap[hostName ?? 'default'] ?? envMap.default;
  }

  public static getKey<K extends keyof EnvConfigType>(
    name: K,
  ): EnvConfigType[K] {
    const env = EnvConfig.getEnv(window.location.hostname);
    const config: EnvConfigType = EnvConfig.configs[env];
    return config[name];
  }
}

/**
 * Returns true if the current running environment is Production
 */
export function isProductionEnv() {
  return EnvConfig.getKey('ENV_NAME') === 'production';
}
